import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { EcommerceBannerPlacement, EcommerceResponse, IEcommerceQuestionSchema } from '../../types'
import Card from './Card'
import style from './SponsoredCarousel.module.scss'

interface ISponsoredCarouselProps {
  schema: IEcommerceQuestionSchema
  findability: boolean
  response: EcommerceResponse
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt?: number) => void
  onSetQuantity: (index: number, qnt: number) => void
}

const SponsoredCarousel = ({
  schema,
  findability,
  response,
  onItemSelect,
  onAddToCart,
  onSetQuantity
}: ISponsoredCarouselProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const trackRef = useRef<HTMLDivElement>(null)
  const [nav, setNav] = useState({ overflowing: false, atStart: true, atEnd: false })

  // Banner-flagged products live only in the BannerAd strip — keep them out of the carousel.
  // Filled by the Featured flag, like the cart's carousel and like Kroger's three — the Sponsored
  // flag keeps only its own job, the label on a card.
  const featured = schema.answers.filter(
    answer => answer.featured && !answer.na && !answer.parent_product && !answer.banner
  )

  const measureNav = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const { scrollLeft, scrollWidth, clientWidth } = track
    const next = {
      overflowing: scrollWidth > clientWidth + 1,
      atStart: scrollLeft <= 1,
      atEnd: scrollLeft + clientWidth >= scrollWidth - 1
    }
    setNav(prev =>
      prev.overflowing === next.overflowing && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next
    )
  }, [])

  // Recompute overflow on track size changes (window resize, font load, image decode).
  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    measureNav()

    const ro = new ResizeObserver(measureNav)
    ro.observe(track)
    return () => ro.disconnect()
  }, [featured.length, measureNav])

  // Re-measure once images load (they affect track width via card aspect-ratio).
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const imgs = Array.from(track.querySelectorAll('img'))
    if (imgs.length === 0) return
    imgs.forEach(img => img.addEventListener('load', measureNav))
    return () => imgs.forEach(img => img.removeEventListener('load', measureNav))
  }, [featured.length, measureNav])

  if (featured.length === 0) return null

  const slide = (direction: 1 | -1) => {
    if (!trackRef.current) return
    const slideWidth = trackRef.current.clientWidth / 2
    trackRef.current.scrollBy({ left: direction * slideWidth, behavior: 'smooth' })
  }

  return (
    <section className={`${style.section} ${viewport.mobile ? style.mobile : ''}`}>
      <h2 className={style.heading}>{t('questions', 'ec', 'chewy', 'shopTheseFinds')}</h2>
      <div className={style.viewport}>
        {!viewport.mobile && nav.overflowing && (
          <button
            type="button"
            className={`${style.navBtn} ${style.navBtnLeft}`}
            disabled={nav.atStart}
            onClick={() => slide(-1)}
            aria-label={t('questions', 'ec', 'chewy', 'previous')}
          >
            <i className="pi pi-chevron-left" />
          </button>
        )}
        <div ref={trackRef} className={style.track} onScroll={measureNav}>
          {featured.map(answer => (
            <div key={answer.id} className={style.slide}>
              <Card
                data-testid="SW-EC-CarouselItem"
                schema={schema}
                answer={answer}
                findability={findability}
                amount={response.answers[answer.index].v}
                disabled={'focus' == schema.mode_type && !answer.focused}
                bordered={true}
                compact={true}
                enableImageGallery={false}
                bannerPlacement="featured"
                onItemSelect={onItemSelect}
                onAddToCart={onAddToCart}
                onSetQuantity={onSetQuantity}
              />
            </div>
          ))}
        </div>
        {!viewport.mobile && nav.overflowing && (
          <button
            type="button"
            className={`${style.navBtn} ${style.navBtnRight}`}
            disabled={nav.atEnd}
            onClick={() => slide(1)}
            aria-label={t('questions', 'ec', 'chewy', 'next')}
          >
            <i className="pi pi-chevron-right" />
          </button>
        )}
      </div>
    </section>
  )
}

export default SponsoredCarousel
